import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, TextField, Typography } from '@mui/material';
import { FormProvider, useForm } from 'react-hook-form';
import { useWindow } from '@client/window/window.hook';
import type { TemplateConfigInput } from '@server/trading-templates/schemas/input.schema';
import type { TradingTemplate } from '@server/trading-templates/schemas/output.schema';
import {
  TradingFormState,
  TradingStrategy
} from '@client/widgets/trading-form/trading-form.types';
import { getStrategyPath } from '@client/widgets/trading-form/strategy/strategy-config-form.utils';
import { StrategyConfigForm } from '@client/widgets/trading-form/strategy/strategy-config-form.component';
import { useTemplateSave } from '@client/features/trading-templates/template-save.hook';
import { AppButton } from '@client/foundation/design-system/components';
import { AdditionalTradingFormContextProvider } from '@client/widgets/trading-form/trading-form-context';
import type { UiStatePath } from '@client/widgets/trading-form/trading-form-ui-state.hook';
import { TradingMode } from '@shared/types';
import type { SignalBlockTarget } from '@server/trading-templates/schemas/input.schema';
import type {
  DefaultTemplatePolicy,
  FormInitSource
} from '@server/trading-layouts/schemas';
import { useApplyTemplate } from '@client/features/trading-templates/load-template/apply-template.hook';
import { useDeleteTemplates } from '@client/features/trading-templates/load-template/delete-templates.hook';
import { useSetDefaultTemplate } from '@client/features/trading-templates/load-template/set-default-template';
import { openManageTradingTemplatesWindow } from '@client/features/trading-templates/manage-trading-templates-window.utils';
import { formStrategyToTemplateValue } from '@client/features/trading-templates/strategy-template-resolver';
import { useSharedTemplateActions } from '@client/features/trading-templates/shared-template-actions.hook';
import { templateEditorSurfaceSx } from './template-editor.styles';

export type StrategyTemplateEditorWindowProps = {
  templateId?: string;
  initialName?: string;
  initialStrategy?: TradingStrategy;
  onSaved?: (savedTemplate: {
    templateId: string;
    name: string;
    config: TemplateConfigInput;
  }) => void;
};

type StrategyTemplateEditorProps = StrategyTemplateEditorWindowProps & {
  onClose: () => void;
};
type StrategyTemplateConfig = Extract<TemplateConfigInput, { type: 'strategy' }>;

const emptyStrategy: TradingStrategy = {};
const strategyPath = getStrategyPath(0);
const initSource: FormInitSource = 'LAYOUT_RESTORE';
const defaultTemplatePolicy: DefaultTemplatePolicy =
  'SKIP_AUTO_APPLY_DEFAULT_TEMPLATES';
const editorMode: TradingMode = 'BACKTESTING';

const buildEditorFormState = (
  strategy: TradingStrategy
): TradingFormState => ({
  timeframes: [
    {
      timeframe: '1m',
      indicators: [],
      strategy
    }
  ]
});

export const useStrategyTemplateEditor = ({
  templateId,
  initialName = '',
  initialStrategy = emptyStrategy,
  onSaved,
  onClose
}: StrategyTemplateEditorProps) => {
  const formApi = useForm<TradingFormState>({
    defaultValues: buildEditorFormState(initialStrategy)
  });
  const { reset, getValues } = formApi;
  const [name, setName] = useState(initialName);
  const [error, setError] = useState('');
  const isEditMode = Boolean(templateId);
  const [uiStateByPath, setUiStateByPath] = useState<Record<string, unknown>>(
    {}
  );

  useEffect(() => {
    setName(initialName);
    reset(buildEditorFormState(initialStrategy));
    setUiStateByPath({});
    setError('');
  }, [initialStrategy, initialName, reset, templateId]);

  const setUiState = useCallback(<T,>(path: UiStatePath, value: T) => {
    const key = path.join('.');
    setUiStateByPath((current) => ({
      ...current,
      [key]: value
    }));
  }, []);

  const getUiState = useCallback(
    <T,>(path: UiStatePath, fallback: T): T => {
      const key = path.join('.');
      return (uiStateByPath[key] as T) ?? fallback;
    },
    [uiStateByPath]
  );

  const { saveTemplate, isPending } = useTemplateSave();
  const loading = isPending;

  const handleSave = useCallback(async () => {
    const trimmedName = name.trim();

    if (!trimmedName) {
      setError('Template name is required.');
      return;
    }

    try {
      const strategy = getValues(strategyPath) ?? {};
      const config: StrategyTemplateConfig = {
        type: 'strategy',
        value: formStrategyToTemplateValue(strategy)
      };

      if (isEditMode && !templateId) {
        setError('Missing template id.');
        return;
      }

      const result = await saveTemplate({
        templateId,
        name: trimmedName,
        isBundle: false,
        config,
        label: 'Strategy template'
      });

      onSaved?.({
        templateId: result.templateId,
        name: trimmedName,
        config: result.config
      });
      onClose();
    } catch (saveError) {
      const message =
        saveError instanceof Error
          ? saveError.message
          : 'Invalid strategy config.';
      setError(message);
    }
  }, [getValues, isEditMode, name, onClose, saveTemplate, templateId]);

  const additionalContextValue = useMemo(
    () => ({
      mode: editorMode,
      initSource,
      defaultTemplatePolicy,
      setUiState,
      getUiState
    }),
    [getUiState, setUiState]
  );

  return {
    formApi,
    isEditMode,
    name,
    error,
    loading,
    setName,
    setError,
    handleSave,
    onClose,
    additionalContextValue
  };
};

export const StrategyTemplateEditor: React.FC<
  StrategyTemplateEditorWindowProps
> = ({ templateId, initialName, initialStrategy, onSaved }) => {
  const { closeWindow, openWindow } = useWindow();
  const { onApply } = useApplyTemplate();
  const { onDelete } = useDeleteTemplates();
  const { onSetAsDefault } = useSetDefaultTemplate();
  const {
    formApi,
    isEditMode,
    name,
    setName,
    setError,
    onClose,
    handleSave,
    error,
    loading,
    additionalContextValue
  } = useStrategyTemplateEditor({
    templateId,
    initialName,
    initialStrategy,
    onSaved,
    onClose: () => closeWindow('StrategyTemplateEditor')
  });
  const openSignalBlockTemplateEditor = useCallback(
    ({
      templateId,
      templateName,
      initialConfig,
      onSaved
    }: {
      templateId?: string;
      templateName: string;
      initialConfig: Extract<TradingTemplate['config'], { type: 'signalBlock' }>;
      onSaved?: (savedTemplate: {
        templateId: string;
        name: string;
        config: TradingTemplate['config'];
      }) => void;
    }) => {
      openWindow({
        type: 'SignalBlockTemplateEditor',
        componentProps: {
          templateId,
          initialName: templateName,
          initialConditions: initialConfig.value.conditions,
          onSaved
        }
      });
    },
    [openWindow]
  );
  const {
    handleShare: handleShareSharedSignalBlock,
    handleImportShared: handleImportSharedSignalBlock,
    handleModifyShared: handleModifySharedSignalBlock,
    handleRevokeShared: handleRevokeSharedSignalBlock
  } = useSharedTemplateActions({
    templateType: 'signalBlock',
    openTemplateEditor: ({
      templateId,
      templateName,
      initialConfig,
      onSaved
    }) => {
      if (initialConfig.type !== 'signalBlock') {
        return;
      }

      openSignalBlockTemplateEditor({
        templateId,
        templateName,
        initialConfig,
        onSaved
      });
    }
  });

  const handleManageSignalBlockTemplates = useCallback(
    ({
      timeframeIndex,
      strategyTarget
    }: {
      timeframeIndex: number;
      strategyTarget: SignalBlockTarget;
    }) => {
      openManageTradingTemplatesWindow({
        openWindow,
        templateType: 'signalBlock',
        strategyTarget,
        onApply: onApply(
          timeframeIndex,
          'signalBlock',
          strategyTarget,
          'replace'
        ),
        onAdd: onApply(timeframeIndex, 'signalBlock', strategyTarget, 'add'),
        onDelete,
        onSetAsDefault: onSetAsDefault('signalBlock'),
        onModify: undefined,
        onShare: (templateId) => handleShareSharedSignalBlock(templateId),
        onImportShared: handleImportSharedSignalBlock,
        onModifyShared: (sharedResourceId) =>
          handleModifySharedSignalBlock(sharedResourceId),
        onRevokeShared: handleRevokeSharedSignalBlock
      });
    },
    [
      handleImportSharedSignalBlock,
      handleModifySharedSignalBlock,
      handleRevokeSharedSignalBlock,
      handleShareSharedSignalBlock,
      onApply,
      onDelete,
      onSetAsDefault,
      openWindow
    ]
  );

  return (
    <FormProvider {...formApi}>
      <AdditionalTradingFormContextProvider value={additionalContextValue}>
        <Box
          sx={{
            ...templateEditorSurfaceSx,
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            height: '100%',
            minHeight: 0
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {isEditMode ? 'Edit Strategy Template' : 'Create Strategy Template'}
          </Typography>

          <Typography variant="body2" color="text.secondary">
            Configure strategy rules for this template.
          </Typography>

          <TextField
            label="Template name"
            value={name}
            onChange={(event) => {
              setName(event.target.value);
              setError('');
            }}
            fullWidth
          />

          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              overflowY: 'auto',
              pr: 0.5
            }}
          >
            <StrategyConfigForm
              strategyPath={strategyPath}
              timeframeIndex={0}
              enableSignalBlockTemplates
              onManageSignalBlockTemplates={handleManageSignalBlockTemplates}
            />
          </Box>

          {error ? (
            <Typography
              variant="caption"
              color="error"
              sx={{ display: 'block' }}
            >
              {error}
            </Typography>
          ) : null}

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
            <AppButton tone="secondary" onClick={onClose} disabled={loading}>
              Cancel
            </AppButton>
            <AppButton tone="primary" onClick={handleSave} disabled={loading}>
              Save
            </AppButton>
          </Box>
        </Box>
      </AdditionalTradingFormContextProvider>
    </FormProvider>
  );
};
